import { Trans } from '@lingui/react/macro';
import { BookingLocationType, BookingRecordDetailsCustomer, BookingStatus } from '@navio/nsdk';
import { NoddiCard, NoddiLink, NoddiSeparator } from 'noddi-ui-web';
import {
  CustomDateFormats,
  customerRoutes,
  getIsUpcomingBooking,
  getShouldGiveFeedback,
  noddiFormatDate,
  stringToUpperCase
} from 'noddi-util';
import { RefObject } from 'react';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { getBookingServiceTitle, getCanDownloadBookingReceipt, isStationaryBooking } from '../../utils/booking';
import { BookingPaymentBadge } from './BookingPaymentBadge';
import { BookingPaymentSummary } from './BookingPaymentSummary';
import { BookingStatusChip } from './BookingStatusChip';
import { BSlugBookingDetailsInfo } from './BSlugBookingDetailsInfo';
import { BSlugFeedbackNudge } from './BSlugFeedbackNudge';
import { ChangeBookingButton } from './ChangeBookingButton';
import { DownloadBookingReceiptButton } from './DownloadBookingReceiptButton';
import { EditComment } from './EditComment';
import { PatchMapPinLocationButton } from './PatchMapPinLocationButton';

interface BSlugDetailsProps {
  booking: BookingRecordDetailsCustomer;
  paymentButtonRef?: RefObject<HTMLDivElement | null>;
}

export const BSlugDetails = ({ booking, paymentButtonRef }: BSlugDetailsProps) => {
  const { currentBrand } = useCurrentBrand();
  const { serviceDepartmentBrand } = booking;

  const isUpcomingBooking = getIsUpcomingBooking(booking);
  const isFeedbackLeft = getShouldGiveFeedback(booking);
  const showComment = isUpcomingBooking && !isStationaryBooking(booking.locationType);
  // The pin tells the technician where the car is parked: nobody comes without a customer visit.
  const showPatchMapPinLocationButton =
    booking.locationType !== BookingLocationType.STATIONARY &&
    booking.hasCustomerVisit &&
    booking.status.value !== BookingStatus.COMPLETED &&
    booking.status.value !== BookingStatus.CANCELLED;

  const deliveryDate = stringToUpperCase(noddiFormatDate(booking.date, CustomDateFormats.FULL_DAY_MONTH));
  const canDownloadReceipt = getCanDownloadBookingReceipt(booking);
  const showServiceProvider = currentBrand !== serviceDepartmentBrand.domain;

  return (
    <NoddiCard
      title={getBookingServiceTitle(booking)}
      description={`${deliveryDate} · ${booking.slug.toUpperCase()}`}
      headerNode={
        <div className='flex flex-wrap items-center gap-2'>
          <BookingStatusChip booking={booking} />
          <BookingPaymentBadge booking={booking} />
        </div>
      }
      banner={isFeedbackLeft && <BSlugFeedbackNudge booking={booking} />}
      footer={
        (canDownloadReceipt || showServiceProvider) && (
          <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-2'>
            {canDownloadReceipt && (
              <DownloadBookingReceiptButton
                booking={booking}
                variant='ghost'
                className='-ml-3 text-primary md:hover:bg-accent'
              />
            )}
            {showServiceProvider && (
              <div className='ml-auto flex items-center gap-3 text-xs text-muted-foreground'>
                <span>
                  <Trans>Service is delivered by</Trans>
                </span>
                {serviceDepartmentBrand.logo ? (
                  <img
                    className='h-6 w-auto max-w-20 object-contain'
                    src={serviceDepartmentBrand.logo.url}
                    alt={serviceDepartmentBrand.name}
                  />
                ) : (
                  <span className='font-medium text-foreground'>{serviceDepartmentBrand.name}</span>
                )}
              </div>
            )}
          </div>
        )
      }
      contentClassName='flex flex-col gap-5'
    >
      <div className='flex flex-col gap-3'>
        <BSlugBookingDetailsInfo booking={booking} />
        {showPatchMapPinLocationButton && <PatchMapPinLocationButton booking={booking} />}

        {(booking.isFullyUnableToComplete || booking.isPartiallyUnableToComplete) && (
          <div className='flex flex-col gap-2'>
            {/* eslint-disable-next-line noddi/no-emoji-in-ui -- deliberate glyph inside a customer-facing copy string, where a NoddiIcon element cannot be embedded */}
            <p>
              ⚠️{' '}
              {booking.isFullyUnableToComplete ? (
                <Trans>Your booking has been unable to complete.</Trans>
              ) : (
                <Trans>Some services in your booking were unable to be completed.</Trans>
              )}
            </p>
            <NoddiLink
              endIcon='ArrowRight'
              className='w-full md:w-fit'
              size='small'
              to={customerRoutes.unableToCompleteDetails.getPath({ slug: booking.slug })}
            >
              <Trans>See more info</Trans>
            </NoddiLink>
          </div>
        )}

        {booking.status.value !== BookingStatus.COMPLETED && <ChangeBookingButton booking={booking} />}
        {showComment && (
          <div className='flex flex-col gap-2'>
            <p className='font-semibold'>
              <Trans>Comment</Trans>
            </p>
            <EditComment bookingId={booking.id} bookingSlug={booking.slug} commentsUser={booking.commentsUser} />
          </div>
        )}
      </div>

      <NoddiSeparator />

      <BookingPaymentSummary booking={booking} paymentButtonRef={paymentButtonRef} />
    </NoddiCard>
  );
};
